import { addDays } from 'date-fns/addDays';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { format } from 'date-fns/format';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfISOWeek } from 'date-fns/startOfISOWeek';
import { subWeeks } from 'date-fns/subWeeks';
import { useMemo } from 'react';

import {
  DAYS_PER_WEEK,
  type DayBucket,
  HeatmapStrip,
  type MonthLabel,
  WEEKS,
} from '../../../components/heatmap-strip';

import type { Event, EventType } from '../../../data/event';
import { formatDateISO } from '../../../helpers/format-date';

export interface StripMeta {
  key: EventType;
  title: string;
  description: string;
  noun: string;
  // Contribution to the day bucket; defaults to 1. `order_edited` sums
  // payload.edit_count (one event = N edits). Named countFor, not valueOf,
  // to avoid clashing with Object.prototype.valueOf.
  countFor?: (event: Event) => number;
}

// Order matters: in the single-column layout STRIPS render top-to-bottom in
// this order; in the two-column layout (2xl+) the grid uses column flow with
// 4 rows, so STRIPS 0-3 fill the left column top-to-bottom and STRIPS 4-7
// fill the right column. Pairs operational events (order/edit/receive/buyup)
// on the left with passive/incidental ones (logs/journal/uploads/AI) on the
// right. Keep this at 8 entries so the 2xl grid stays a clean 4×2.
export const STRIPS: StripMeta[] = [
  {
    key: 'order_sent',
    title: 'Orders Sent',
    description: 'An order was sent to a vendor.',
    noun: 'orders sent',
  },
  {
    key: 'order_edited',
    title: 'Order Edits',
    description: 'A line was edited on an open order. Counts every edit, not just the first.',
    noun: 'order edits',
    countFor: (e) => (e.event_type === 'order_edited' ? e.payload.edit_count : 1),
  },
  {
    key: 'receiving_completed',
    title: 'Receiving',
    description: 'A receiving session was marked complete.',
    noun: 'receivings',
  },
  {
    key: 'buyup_engaged',
    title: 'Buyups / Deals',
    description: 'A quantity was changed on the buyup screen. Counts the first change per visit.',
    noun: 'buyup work-sessions',
  },
  {
    key: 'exception_logs_engaged',
    title: 'Exception logs',
    description:
      'A filter or search was changed on the exception logs report. Counts the first change per visit.',
    noun: 'exception-log sessions',
  },
  {
    key: 'sales_journal_engaged',
    title: 'Sales journal',
    description:
      'A date range or search was changed on the sales journal report. Counts the first change per visit.',
    noun: 'sales-journal sessions',
  },
  {
    key: 'store_upload',
    title: 'Store uploads',
    description: 'An LPOS upload from the desktop app finished processing.',
    noun: 'uploads',
  },
  {
    key: 'mcp_tool_used',
    title: 'AI assistant',
    description:
      'A tool was called from a connected AI assistant (Claude, ChatGPT). Counts every tool call.',
    noun: 'AI tool calls',
  },
];

const STRIPS_BY_KEY = new Map(STRIPS.map((s) => [s.key, s]));

export interface HeatmapData {
  strips: Record<EventType, DayBucket[][]>;
  monthLabels: MonthLabel[];
}

export function buildHeatmapData(events: Event[], now: Date): HeatmapData {
  const today = startOfDay(now);
  const currentWeekMonday = startOfISOWeek(today);
  const firstWeekMonday = subWeeks(currentWeekMonday, WEEKS - 1);

  const strips = {} as Record<EventType, DayBucket[][]>;
  for (const strip of STRIPS) {
    const grid: DayBucket[][] = [];
    for (let w = 0; w < WEEKS; w++) {
      const week: DayBucket[] = [];
      for (let d = 0; d < DAYS_PER_WEEK; d++) {
        const date = addDays(firstWeekMonday, w * DAYS_PER_WEEK + d);
        week.push({ date, dateKey: formatDateISO(date), count: 0, visible: date <= today });
      }

      grid.push(week);
    }

    strips[strip.key] = grid;
  }

  for (const event of events) {
    const strip = STRIPS_BY_KEY.get(event.event_type);
    if (!strip) {
      continue;
    }

    const dayOffset = differenceInCalendarDays(startOfDay(event.created_at), firstWeekMonday);
    if (dayOffset < 0 || dayOffset >= WEEKS * DAYS_PER_WEEK) {
      continue;
    }

    const w = Math.floor(dayOffset / DAYS_PER_WEEK);
    const d = dayOffset % DAYS_PER_WEEK;
    strips[event.event_type][w][d].count += strip.countFor ? strip.countFor(event) : 1;
  }

  const monthLabels: MonthLabel[] = [];
  let lastMonth = -1;
  for (let w = 0; w < WEEKS; w++) {
    const firstDay = strips[STRIPS[0].key][w][0].date;
    const m = firstDay.getMonth();
    if (m !== lastMonth) {
      monthLabels.push({ weekIndex: w, label: format(firstDay, 'MMM') });
      lastMonth = m;
    }
  }

  return { strips, monthLabels };
}

function tooltipFor(bucket: DayBucket, noun: string): string {
  const dateLabel = format(bucket.date, 'EEE, MMM d');

  return bucket.count > 0 ? `${dateLabel}: ${bucket.count} ${noun}` : `${dateLabel}: no activity`;
}

interface TimelineHeatmapProps {
  events: Event[];
  now: Date;
  onCellClick: (dateKey: string) => void;
}

export function TimelineHeatmap({ events, now, onCellClick }: TimelineHeatmapProps) {
  const data = useMemo(() => buildHeatmapData(events, now), [events, now]);

  return (
    // Each strip is a fixed ~752px wide. Stack vertically on narrow viewports;
    // pair into two columns once the container can fit them side-by-side. The
    // 2-column layout uses column flow + 4 rows so the STRIPS order fills the
    // left column first, then the right (see comment on STRIPS).
    <div className="grid grid-cols-1 gap-x-8 gap-y-4 2xl:grid-flow-col 2xl:grid-cols-2 2xl:grid-rows-4">
      {STRIPS.map((strip) => (
        <HeatmapStrip
          key={strip.key}
          title={strip.title}
          description={strip.description}
          weeks={data.strips[strip.key]}
          monthLabels={data.monthLabels}
          cellTooltip={(bucket) => tooltipFor(bucket, strip.noun)}
          onCellClick={onCellClick}
        />
      ))}
    </div>
  );
}
